import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { BellOff } from "lucide-react";
import api from "../services/api";

export const ALERT_TYPE_LABELS = {
  LOW_STOCK_IMPORT: "Stock short after invoice upload",
  LOW_STOCK_LOCK_DATE: "Stock short at lock date",
  PURCHASE_CREDIT_DUE: "Purchase credit due",
};

const SEVERITY_BADGE = { CRITICAL: "badge-red", WARNING: "badge-amber", INFO: "badge-blue" };

const fmtDateTime = (value) => (value ? new Date(value).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "-");

export const suppressAlert = (id) => api.post(`/api/v1/alerts/${id}/suppress`, {});

/**
 * Every alert raised for the company — stock short after an invoice upload or at the lock date, purchase bills on credit falling
 * due. Active alerts also scroll along the top of the panel until suppressed; a suppressed alert never shows again.
 */
const Alerts = ({ onChanged }) => {
  const [alerts, setAlerts] = useState([]);
  const [showSuppressed, setShowSuppressed] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.get("/api/v1/alerts", { all: true });
      setAlerts(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(error.message || "Failed to load alerts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const suppress = async (alert) => {
    try {
      await suppressAlert(alert.id);
      toast.success("Alert suppressed — it will not show again");
      await load();
      onChanged?.();
    } catch (error) {
      toast.error(error.message || "Could not suppress the alert");
    }
  };

  const shown = alerts.filter((a) => showSuppressed || !a.suppressed);
  const activeCount = alerts.filter((a) => !a.suppressed).length;

  return (
    <div className="p-4" style={{ position: "relative" }}>
      <div className="page-subtitle" style={{ marginBottom: 12 }}>
        Alerts raised for the company: products whose closing stock is short after a sales invoice upload or when the lock date is set,
        and purchase bills bought on credit falling due (from the day before the credit date). Active alerts scroll along the top of the
        panel. Suppress an alert once it is handled — it will not show again.
      </div>
      <div className="filters-bar">
        <span>
          <strong>{activeCount}</strong> active alert(s)
        </span>
        <label style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
          <input type="checkbox" checked={showSuppressed} onChange={(e) => setShowSuppressed(e.target.checked)} />
          Show suppressed
        </label>
        <button className="secondary" onClick={load} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Raised</th>
              <th>Type</th>
              <th>Severity</th>
              <th style={{ textAlign: "left" }}>Alert</th>
              <th>SMS</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <div className="empty-state">
                    <span className="empty-state-title">No alerts</span>
                  </div>
                </td>
              </tr>
            ) : (
              shown.map((alert) => (
                <tr key={alert.id} style={alert.suppressed ? { opacity: 0.55 } : undefined}>
                  <td>{fmtDateTime(alert.createdAt)}</td>
                  <td>{ALERT_TYPE_LABELS[alert.type] || alert.type}</td>
                  <td>
                    <span className={`badge ${SEVERITY_BADGE[alert.severity] || "badge-gray"}`}>{alert.severity}</span>
                  </td>
                  <td style={{ textAlign: "left", whiteSpace: "normal", maxWidth: 640 }}>
                    <strong>{alert.title}</strong>
                    <div className="page-subtitle">{alert.message}</div>
                    {alert.showFrom ? <div className="page-subtitle">Shown from {alert.showFrom}</div> : null}
                  </td>
                  <td>{alert.smsSentAt ? fmtDateTime(alert.smsSentAt) : "-"}</td>
                  <td>
                    {alert.suppressed ? (
                      <span title={`by ${alert.suppressedBy || "-"} on ${fmtDateTime(alert.suppressedAt)}`} className="badge badge-gray">
                        Suppressed
                      </span>
                    ) : (
                      <span className="badge badge-red">Active</span>
                    )}
                  </td>
                  <td>
                    {!alert.suppressed ? (
                      <button className="secondary" onClick={() => suppress(alert)} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <BellOff size={14} /> Suppress
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Alerts;
